import React from 'react';
import { Button } from '../Button';
import { intl } from '../../../lib';

export type TablePaginationProps = {
  tableInstance?: any;
  dynamicOptionsCount?: number;
};

export const TablePagination = ({
  tableInstance,
  dynamicOptionsCount = 3
}: TablePaginationProps): JSX.Element => {
  const {
    canPreviousPage,
    canNextPage,
    previousPage,
    nextPage,
    gotoPage,
    pageOptions,
    pageCount,
    state: { pageIndex }
  } = tableInstance;

  const getDynamicOptions = (): number[] => {
    let startIndex;
    const dynamicOptions: number[] = [];

    if (pageIndex < dynamicOptionsCount + 1) {
      startIndex = 1;
    } else if (pageIndex > pageCount - (dynamicOptionsCount + 1)) {
      startIndex = pageCount - (dynamicOptionsCount + 1);
    } else {
      startIndex = pageIndex - Math.floor(dynamicOptionsCount / 2);
    }

    for (let i = startIndex; i < startIndex + dynamicOptionsCount; i++) {
      dynamicOptions.push(i);
    }

    return dynamicOptions;
  };

  const renderPageOptions = (): JSX.Element | null => {
    const currentPageIndex = pageIndex;
    const options = pageOptions;

    if (options.length <= 5) {
      return options.map((pageOption: number) => (
        <Button
          key={`page-button-${pageOption}`}
          variant="gray"
          aria-label={`${intl('view.toolbar.paging.beforeText')} ${
            pageOption + 1
          }`}
          text={`${pageOption + 1}`}
          className={pageOption === currentPageIndex ? 'active' : ''}
          onClick={() => gotoPage(pageOption)}
        />
      ));
    }

    return (
      <>
        <Button
          variant="gray"
          aria-label={`${intl('view.toolbar.paging.beforeText')} 1`}
          text="1"
          className={currentPageIndex === 0 ? 'active' : ''}
          onClick={() => gotoPage(0)}
        />
        {currentPageIndex > dynamicOptionsCount && '...'}
        {getDynamicOptions().map((pageOption: number) => (
          <Button
            variant="gray"
            aria-label={`${intl('view.toolbar.paging.beforeText')} ${
              pageOption + 1
            }`}
            text={`${pageOption + 1}`}
            key={`dynamic-options-${pageOption}`}
            className={currentPageIndex === pageOption ? 'active' : ''}
            onClick={() => gotoPage(pageOption)}
          />
        ))}
        {currentPageIndex + 1 + Math.ceil(dynamicOptionsCount / 2) <
          pageCount && '...'}
        <Button
          variant="gray"
          aria-label={`${intl('view.toolbar.paging.beforeText')} ${pageCount}`}
          text={`${pageCount}`}
          className={currentPageIndex === pageCount - 1 ? 'active' : ''}
          onClick={() => gotoPage(pageCount - 1)}
        />
      </>
    );
  };

  return (
    <div className="ehs-table-pagination">
      <Button
        variant="gray"
        aria-label={intl('view.button.previous')}
        text={`${intl('view.button.previous')}`}
        onClick={() => previousPage()}
        disabled={!canPreviousPage}
        icon="arrow-left"
      />
      {renderPageOptions()}
      <Button
        variant="gray"
        aria-label={intl('view.button.next')}
        text={intl('view.button.next')}
        onClick={() => nextPage()}
        disabled={!canNextPage}
        icon="arrow-right"
      />
    </div>
  );
};
